import { defineStore, storeToRefs } from 'pinia';
import type { Cart, CartAnimal, OutcomeForm, RtoFee, StepData } from './interfaces';
import type { CartRouteStage } from './types';
import type moment from 'moment';
import { useSessionStore } from '@Stores';
import { INTAKE_TYPES } from '@IntakeModule';
import { CART_TYPE_ROUTES, INTAKES_WITH_PAYMENT, OUTCOME_ROUTES, OUTCOMES_WITH_CART } from './constants';
import { CART_STEPS, CART_TYPES, PAYMENT_METHODS } from './enums';
import { parseCartDateMoment } from './helpers/parseCartDate.helper';
import {
  GetCartActiveDiscounts,
  GetCartSessionService,
  GetCashDrawerOptionsService,
  GetLostAndFoundOutcomeFormService,
  GetOutcomeDetails,
  GetOutcomeFormsService,
  GetRehomeOutcomeFormService,
  GetRtoFeesService,
} from './services';

// NOTE :: The single place a cart type turns into a URL. Unknown types fall back to the outcome
// routes, which is what the old per-type switch statements did.
function buildCartRoute(cartId: string, cartType: string | undefined, stage: CartRouteStage) {
  const routes = CART_TYPE_ROUTES[cartType ?? ''] ?? OUTCOME_ROUTES;

  return `${routes[stage]}${cartId}`;
}

interface TriggerCompleteOutcomeSignal {
  isMobile: boolean;
  signal: number;
}

interface FosterCheckoutSharedState {
  fosterStartDate: Date | null;
  fosterContractId: string | null;
  animalOverrides: Record<string, { waiver_id?: string; waiver_ids?: string[]; notes?: string }>;
}

function createEmptyFosterCheckoutShared(): FosterCheckoutSharedState {
  return {
    fosterStartDate: null,
    fosterContractId: null,
    animalOverrides: {},
  };
}

// TODO - AF-527 :: Each step should have a proper interface
interface State {
  activeCartStep: number;
  cardFieldError: string;
  cardHolderName: string;
  cartAside: boolean;
  cartAsideButton: boolean;
  cartData: any;
  cashDrawerOptions: any;
  checkoutPendingData: any; // TODO : AF-530 Probably deprecated
  confirmingCartWithTotalZero: null | boolean;
  discounts: any;
  emptyCartConfirmCallback: (() => void | Promise<void>) | null;
  fetchingCart: boolean; // TODO : AF-528 Still needs implementation
  forms: OutcomeForm[];
  fosterEquipmentAvailable: boolean;
  fosterCheckoutShared: FosterCheckoutSharedState;
  hasRtoFee: boolean;
  hasWaivers: boolean;
  loading: boolean;
  mainLoading: boolean;
  outcomeCheckoutType: any;
  outcomeCompleted: boolean;
  outcomeForm: any; // TODO : AF-530 Probably deprecated
  outcomeInfoActiveStep: number; // TODO : AF-530 Probably deprecated
  pendingIntakePersonFromProfile: any;
  peopleForm: any;
  redirectUrl: string;
  rtoFees: RtoFee[];
  selectedRtoFee: RtoFee[];
  stepState: StepData;
  triggerCompleteOutcomeSignal: TriggerCompleteOutcomeSignal | null;
}

const initialStepState = {
  [CART_STEPS.ADD_PEOPLE]: {},
  [CART_STEPS.DONATION]: {},
  [CART_STEPS.EQUIPMENT_RENTAL_CHECKOUT]: {},
  [CART_STEPS.CHECKOUT_METHOD]: {},
  [CART_STEPS.MEDICAL_COSTS]: {},
  [CART_STEPS.MOBILE_CHECKOUT]: {},
  [CART_STEPS.MOBILE_PROCESSING]: {},
  [CART_STEPS.OUTCOME_INFO]: {},
  [CART_STEPS.PAYMENT_METHOD]: {},
  [CART_STEPS.PRODUCTS_AND_SERVICES]: {},
  [CART_STEPS.PRODUCTS_INFO]: {},
  [CART_STEPS.SUMMARY]: {},
};

const STATE: State = {
  activeCartStep: 0,
  cardFieldError: '',
  cardHolderName: '',
  cartAside: false,
  cartAsideButton: true,
  cartData: {},
  cashDrawerOptions: [],
  checkoutPendingData: {},
  confirmingCartWithTotalZero: null,
  discounts: [],
  emptyCartConfirmCallback: null,
  fetchingCart: false, // TODO : AF-528 Still needs implementation
  forms: [],
  fosterEquipmentAvailable: false,
  fosterCheckoutShared: createEmptyFosterCheckoutShared(),
  hasRtoFee: false,
  hasWaivers: false,
  loading: false,
  mainLoading: true,
  outcomeCheckoutType: null,
  outcomeCompleted: false,
  outcomeForm: {}, // TODO : AF-530 Probably deprecated
  outcomeInfoActiveStep: 0,
  pendingIntakePersonFromProfile: null,
  peopleForm: undefined,
  redirectUrl: '',
  rtoFees: [],
  selectedRtoFee: [],
  stepState: initialStepState,
  triggerCompleteOutcomeSignal: null,
};

export default defineStore('shoppingCart', {
  state: (): State => ({
    ...STATE,
    fosterCheckoutShared: createEmptyFosterCheckoutShared(),
  }),

  getters: {
    // TODO - AF-529 - All getters should be using getActions
    activeDiscount: (state: State) => state.cartData?.discount || 0,
    activeDiscountCode: (state: State) => state.cartData?.discount_code || '',
    animals: (state: State) => state.cartData?.animals || [],
    anonymousDonations: (state: State) => state.cartData?.is_anonymous_donation,
    cartId: (state: State) => state.cartData?.cart_id?.$oid || state.cartData?.cart_id || '',
    cartTotal: (state: State) => Number(state.cartData?.total) || 0,
    citations: (state: State) => state.cartData?.citations || [],
    donations: (state: State) => state.cartData?.donations_details || [],
    isAdoption: (state: State) => state.cartData?.outcome_type === 'adopted',
    isCheckoutPending: (state: State) => state.cartData?.checkout_status === 'pending',
    isCitation: (state: State) => state.cartData?.cart_type === 'citation',
    isDonation: (state: State) => state.cartData?.cart_type === 'donation',
    isEuthanasia: (state: State) => state.cartData?.outcome_type === INTAKE_TYPES.OWNER_REQUESTED_EUTHANASIA,
    isFoster: (state: State) => state.cartData?.cart_type === 'foster',
    isEquipmentRental: (state: State) => state.cartData?.cart_type === 'equipment_rental',
    isIntake: (state: State) => state.cartData?.cart_type === 'intake',
    isIntakeWithNoFees: (state: State) => !state.cartData?.has_fees,
    isOutcome: (state: State) => state.cartData?.cart_type === 'outcome',
    isProduct: (state: State) => state.cartData?.cart_type === 'product',
    isReturnToOwner: (state: State) => state.cartData?.outcome_type === 'return_to_owner',
    isService: (state: State) => state.cartData?.cart_type === 'service',
    isTransferOut: (state: State) => state.cartData?.outcome_type === 'transfer_out',
    outcomeTitle: (state: State) => state.cartData?.outcome_title || '',
    outcomeType: (state: State) => state.cartData?.outcome_type || '',
    people: (state: State) => state.cartData?.people || undefined,
    products: (state: State) => state.cartData?.products || [],
    productsLength: (state: State) => state.cartData?.products?.length,
    services: (state: State) => state.cartData?.services || [],
    servicesLength: (state: State) => state.cartData?.services?.length,
    status: (state: State) => state.cartData?.status || undefined,
    type: (state: State) => state.cartData?.cart_type || undefined,

    hasCart: (state: State) => {
      return (
        OUTCOMES_WITH_CART.includes(state.cartData?.outcome_type) ||
        (state.cartData?.cart_type === 'intake' && INTAKES_WITH_PAYMENT.includes(state.cartData?.outcome_type))
      );
    },

    completeOutcomeRoute(state: State) {
      const cartId = state.cartData?.cart_id?.$oid || state.cartData?.cart_id;
      if (!cartId) return null;

      return buildCartRoute(cartId, state.cartData?.cart_type, 'completed');
    },

    pendingOutcomeRoute(state: State) {
      const cartId = state.cartData?.cart_id?.$oid || state.cartData?.cart_id;
      if (!cartId) return null;

      return buildCartRoute(cartId, state.cartData?.cart_type, 'pending');
    },

    cartLength: (state: State) =>
      (state.cartData?.animals ? state.cartData?.animals.length : 0) +
      (state.cartData?.citations ? state.cartData?.citations.length : 0) +
      (state.cartData?.products ? state.cartData?.products.length : 0) +
      (state.cartData?.donations_details ? state.cartData?.donations_details.length : 0) +
      (state.cartData?.services ? state.cartData?.services.length : 0) +
      (state.cartData?.equipment_lines && Array.isArray(state.cartData.equipment_lines)
        ? state.cartData.equipment_lines.length
        : 0),

    emptyCart: (state: State) =>
      !state.cartData?.is_anonymous_donation &&
      (!state.cartData?.animals || !state.cartData?.animals.length) &&
      (!state.cartData?.citations || !state.cartData?.citations.length) &&
      (!state.cartData?.services || !state.cartData?.services.length) &&
      (!state.cartData?.donations_details || !state.cartData?.donations_details.length) &&
      (!state.cartData?.products || !state.cartData?.products.length) &&
      (!state.cartData?.equipment_lines || !state.cartData.equipment_lines.length),

    outcomeHasWaivers: (state: State) =>
      state.hasWaivers || state.cartData?.animals?.some(({ waivers }: CartAnimal) => waivers?.length > 0) || false,
  },

  actions: {
    // GETTERS
    cartTypeIsCitation: (cartData: any) => cartData?.cart_type === 'citation',
    cartTypeIsDonation: (cartData: any) => cartData?.cart_type === 'donation',
    cartTypeIsFoster: (cartData: any) => cartData?.cart_type === 'foster',
    cartTypeIsIntake: (cartData: any) => cartData?.cart_type === 'intake',
    cartTypeIsOutcome: (cartData: any) => cartData?.cart_type === 'outcome',
    cartTypeIsProduct: (cartData: any) => cartData?.cart_type === 'product',
    cartTypeIsService: (cartData: any) => cartData?.cart_type === 'service',
    getAnimals: (cartData: any) => cartData?.animals || [],
    getCitations: (cartData: any) => cartData?.citations || [],
    getDonations: (cartData: any) => cartData.donations_details || [],
    getIntakeHasNoFees: (cartData: any) => !cartData?.has_fees,
    getOutcomeTitle: (cartData: any) => cartData?.outcome_title || '',
    getOutcomeType: (cartData: any) => cartData?.outcome_type || '',
    getProducts: (cartData: any) => cartData?.products || [],
    getServices: (cartData: any) => cartData?.services || [],
    getStatus: (cartData: any) => cartData?.status,
    getType: (cartData: any) => cartData?.cart_type,

    getCompletedOutcomeRoute(cartId: string, cartType?: string) {
      return buildCartRoute(cartId, cartType, 'completed');
    },

    getHasCart: (cartData: any) => {
      return (
        OUTCOMES_WITH_CART.includes(cartData?.outcome_type) ||
        (cartData?.cart_type === 'intake' && INTAKES_WITH_PAYMENT.includes(cartData?.outcome_type))
      );
    },

    getPendingOutcomeRoute(cartId: string, cartType?: string) {
      return buildCartRoute(cartId, cartType, 'pending');
    },

    getActiveOutcomeRoute(cartId: string, cartType?: string) {
      return buildCartRoute(cartId, cartType, 'active');
    },

    getCartLength(cartData: any) {
      return (
        (cartData?.animals ? cartData?.animals.length : 0) +
        (cartData?.citations ? cartData?.citations.length : 0) +
        (cartData?.products ? cartData?.products.length : 0) +
        (cartData?.donations_details ? cartData.donations_details.length : 0) +
        (cartData?.services ? cartData?.services.length : 0) +
        (cartData?.equipment_lines && Array.isArray(cartData.equipment_lines) ? cartData.equipment_lines.length : 0)
      );
    },

    getNewestCartAnimalIntakeDate(cartData?: Cart): Date | null {
      const animals: CartAnimal[] = cartData?.animals ?? this.cartData?.animals ?? [];
      if (!animals.length) return null;

      let newestIntakeMoment: moment.Moment | null = null;

      for (const animal of animals) {
        const parsed = parseCartDateMoment(animal.intake_date);
        if (!parsed) continue;

        if (!newestIntakeMoment || parsed.isAfter(newestIntakeMoment)) {
          newestIntakeMoment = parsed;
        }
      }

      return newestIntakeMoment?.toDate() ?? null;
    },

    isIntakeInWithNoPayment(cartData: any) {
      return (
        cartData?.cart_type === 'intake' && !Number(cartData?.donation || 0) && !Number(cartData?.total_charge || 0)
      );
    },

    showPaymentSummary(cartData: any): boolean {
      if (!cartData) return false;

      const { CITATION, DONATION, EQUIPMENT_RENTAL, FOSTER, PRODUCT, INTAKE } = CART_TYPES;
      const { cart_type, outcome_type } = cartData;

      if (cart_type === FOSTER || cart_type === EQUIPMENT_RENTAL) return false;

      if ([CITATION, DONATION, PRODUCT].includes(cart_type)) return true;

      if (cart_type === INTAKE) return !(this.isIntakeInWithNoPayment(cartData) || this.getIntakeHasNoFees(cartData));

      if (!OUTCOMES_WITH_CART.includes(outcome_type)) return false;

      return true;
    },

    isCardReaderLastFourInvalid(digits?: number, paymentMethod?: PAYMENT_METHODS) {
      return paymentMethod === PAYMENT_METHODS.CARD_READER && (!digits || digits.toString().length !== 4);
    },

    setCompleteOutcomeTriggerSignal(isMobile: boolean) {
      this.triggerCompleteOutcomeSignal = {
        isMobile,
        signal: Number(new Date().getTime()),
      };
    },

    // PROVIDERS
    // TODO - AF-528 - Review this logic to hit the API only the necessary amount of time.
    async fetchCompletedOutcome(cartId: string) {
      return GetOutcomeDetails(cartId)
        .then((data: any) => {
          // NOTE : Empty cart
          if (!data?.cart_id?.$oid) return;

          // NOTE : Mobile checkout
          const isCheckoutPending = data.checkout_status === 'pending';
          if (isCheckoutPending) {
            this.checkoutPendingData = data;
            this.redirectUrl = this.getPendingOutcomeRoute(data.cart_id.$oid, data.cart_type);
            return;
          }

          // NOTE : Outcome completed
          this.outcomeCompleted = true;
          this.redirectUrl = this.getCompletedOutcomeRoute(data.cart_id.$oid, data.cart_type);
          return;
        })
        .finally(() => (this.fetchingCart = false));
    },

    async fetchCashDrawerOptions() {
      const { shelterProfile } = storeToRefs(useSessionStore());
      if (!shelterProfile.value?.cash_drawer) return;

      return await GetCashDrawerOptionsService().then((data: any) => (this.cashDrawerOptions = data));
    },

    async fetchActiveCart(cartIdParam?: string) {
      this.fetchingCart = true;

      return GetCartSessionService(cartIdParam)
        .then((data: any) => {
          const cartId = data?.cart_id?.$oid || data?.cart_id;
          if (!cartId && cartIdParam) this.fetchCompletedOutcome(cartIdParam);

          this.cartData = data;
        })
        .finally(() => (this.fetchingCart = false));
    },

    async fetchFosterEquipmentAvailability() {
      // Skip the network call entirely when the shelter has the feature flag off — the inventory
      // collection may not even exist yet, and the optional Equipment Rental step inside Foster
      // checkout is gated on `equipment_rental_enabled` anyway.
      if (!useSessionStore().shelterProfile?.equipment_rental_enabled) {
        this.fosterEquipmentAvailable = false;
        return;
      }

      // NOTE: Late import to avoid a circular dep between the cart module and the equipment rental module.
      const { getEquipmentRentalFosterAvailabilityService } = await import('../EquipmentRental/services');
      const result = await getEquipmentRentalFosterAvailabilityService();
      this.fosterEquipmentAvailable = !!result?.has_available_foster_equipment;
    },

    async fetchActiveDiscounts() {
      return GetCartActiveDiscounts().then((data: any) => (this.discounts = data));
    },

    async fetchOutcomeForms(displayFormType?: string) {
      this.loading = true;

      let formService: () => Promise<OutcomeForm[]>;

      //NOTE : Do not use pretter-ignore here as formService() needs to be called
      switch (displayFormType) {
        case 'outcome':
          formService = GetOutcomeFormsService;
          break;
        case 'lost_found':
          formService = GetLostAndFoundOutcomeFormService;
          break;
        case 'rehome':
          formService = GetRehomeOutcomeFormService;
          break;
        default:
          return Promise.reject(new Error('Invalid form type'));
      }

      return formService()
        .then((data: OutcomeForm[]) => {
          this.forms = data;
          this.getRtoFees();
        })
        .finally(() => (this.loading = false));
    },

    async getRtoFees() {
      await GetRtoFeesService().then(({ rto_fees }: any) => (this.hasRtoFee = !!rto_fees.length));
    },

    async refreshData() {
      this.loading = true;
      return this.fetchActiveCart(this.$state.cartData.cart_id.$oid).finally(() => (this.loading = false));
    },

    // RESETS
    resetCart() {
      this.$state = {
        ...STATE,
        cartData: {},
        discounts: this.discounts,
        forms: this.forms,
        fosterCheckoutShared: createEmptyFosterCheckoutShared(),
        hasRtoFee: this.hasRtoFee,
        emptyCartConfirmCallback: null,
        pendingIntakePersonFromProfile: null,
        selectedRtoFee: [],
        rtoFees: [],
      };
    },

    outcomeCompletedCartReset() {
      // NOTE : This method is here in case we need to do specific resets for completed outcomes
      this.resetCart();

      return true;
    },
  },
});
